import type { FieldDefaults } from './defaults';
import { fieldType } from './registry';
import { resolveField, valueAt } from './resolve';
import type { FieldDef, FieldValues } from './types';

export type FieldErrors = Record<string, string>;

function isBlank(value: unknown): boolean {
  return value == null || (typeof value === 'string' && !value.trim()) || (Array.isArray(value) && !value.length);
}

function ruleError(def: FieldDef<unknown>, value: unknown): string | undefined {
  const v = def.validate ?? {};
  const label = typeof def.label === 'string' ? def.label : def.name;
  if (typeof value === 'number') {
    if (v.min != null && value < v.min) return v.message ?? `${label} must be at least ${v.min}.`;
    if (v.max != null && value > v.max) return v.message ?? `${label} must be at most ${v.max}.`;
  }
  if (typeof value === 'string') {
    if (v.minLength != null && value.length < v.minLength) return v.message ?? `${label} needs at least ${v.minLength} characters.`;
    if (v.maxLength != null && value.length > v.maxLength) return v.message ?? `${label} can have at most ${v.maxLength} characters.`;
    if (v.pattern != null && !new RegExp(v.pattern).test(value)) return v.message ?? `${label} is not in the expected format.`;
  }
  return undefined;
}

/**
 * Check `values` against the definitions; returns {name: message} for each
 * failing field. Hidden fields are skipped, so a field shown only in some
 * circumstances is required only in those. The server still checks every save.
 */
export function validateFields(defs: FieldDef<unknown>[], values: FieldValues, defaults: FieldDefaults): FieldErrors {
  const errors: FieldErrors = {};
  for (const def of defs) {
    const field = resolveField(def, defaults, values);
    if (field.hidden || field.disabled) continue;
    const value = valueAt(values, def.name);
    const label = typeof def.label === 'string' ? def.label : def.name;
    const message = isBlank(value)
      ? (def.required ? def.validate?.message ?? `${label} is required.` : undefined)
      : fieldType(def.type).check?.(value, field) ?? ruleError(def, value) ?? def.validate?.custom?.(value, values);
    if (message) errors[def.name] = message;
  }
  return errors;
}
